/**
 * BacktrayLinkInline (Iter234) — back-design of Iter233's Dektite link.
 * ───────────────────────────────────────────────────────────────────
 * Lives on every Aquaseal/Dektite component_line row. Closes the loop
 * with Iter233 by letting the operator link a Backtray Flashing line
 * to a Dektite from the Aquaseal side.
 *
 * Two-tab picker:
 *   • "Pick existing" — dropdown of every Flashing row on the appraisal.
 *                       Backtray-category rows are grouped/highlighted
 *                       at the top; other Flashings appear below.
 *   • "Create new"   — embeds FlashingTemplatePickerInline pre-filtered
 *                       to Backtray templates; width auto-suggested
 *                       from the Dektite's Ø.
 *
 * One link per Dektite (object, not array — locked in by the user).
 *
 * Chip actions when a link exists:
 *   ✎ Change          — re-open picker for a swap
 *   Unlink            — clear the link; reciprocal `linked_dektites`
 *                       entry on the Flashing row is also cleared.
 *                       Flashing row itself stays.
 *   🗑 Unlink + delete — only visible when `source === 'created'` (the
 *                       Flashing row was auto-added by THIS cascade).
 *                       Removes link AND deletes the Flashing row.
 *
 * Parent contract (passed down through ComponentLineRow):
 *   onLinkBacktray(payload)
 *   onUnlinkBacktray({ deleteFlashing })
 *   onChangeBacktray(payload)
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import FlashingTemplatePickerInline from '@/components/shared/ComponentRow/FlashingTemplatePickerInline';

// Ø → suggested Backtray width (mm). Lookup table per the user's spec.
// Falls back to 700 mm when no rule matches (most common Backtray size).
const widthSuggestForDia = (diaMm) => {
  const d = Number(diaMm) || 0;
  if (d <= 0) return 700;
  if (d <= 50)  return 700;
  if (d <= 80)  return 700;
  if (d <= 100) return 900;
  if (d <= 125) return 900;
  if (d <= 150) return 1100;
  return 1100;
};

const fmtChip = (link) => {
  if (!link) return '';
  const tn = link.type_name || link.template_name || 'Backtray';
  const w  = link.width_mm ? `${link.width_mm}W` : '';
  const l  = link.length_m ? `${Number(link.length_m).toFixed(2)}m` : '';
  return [tn, w, l].filter(Boolean).join(' · ');
};

export default function BacktrayLinkInline({
  ref_id,                    // e.g. "AQ1"
  dektiteDiaMm = 0,
  roofProfileCtx = '',
  linkedBacktray = null,     // { uid, line_key, template_id, template_name, type_name, width_mm, length_m, source }
  flashingLines = [],        // [{ _key, type_name, flashing_type, ... }] — all Flashing rows on this appraisal
  flashingTemplates = [],    // full template catalogue, used for "Create new" tab
  onLinkBacktray,
  onUnlinkBacktray,
  onChangeBacktray,
}) {
  const [picking, setPicking] = useState(false);
  const [tab, setTab] = useState('pick');             // 'pick' | 'create'
  const [pickedLineKey, setPickedLineKey] = useState('');
  const [pickedTemplate, setPickedTemplate] = useState(null);
  const [draftWidthMm, setDraftWidthMm] = useState(() => widthSuggestForDia(dektiteDiaMm));
  const [draftLengthM, setDraftLengthM] = useState(1);
  const [changing, setChanging] = useState(false);

  // Backtray templates — keyed first so "Create new" defaults sensibly.
  const backtrayTemplates = useMemo(
    () => flashingTemplates.filter(t => /tray/i.test(t.category || '')),
    [flashingTemplates]
  );

  // Existing Flashing rows split into Backtrays vs. Other (per user spec
  // — Backtrays default first, others available below).
  const { backtrayRows, otherRows } = useMemo(() => {
    const isBacktray = (l) => /tray|backtray|btray/i.test((l.flashing_type || '') + ' ' + (l.type_name || ''));
    return {
      backtrayRows: flashingLines.filter(isBacktray),
      otherRows: flashingLines.filter(l => !isBacktray(l)),
    };
  }, [flashingLines]);

  const reset = () => {
    setPicking(false);
    setChanging(false);
    setTab('pick');
    setPickedLineKey('');
    setPickedTemplate(null);
    setDraftWidthMm(widthSuggestForDia(dektiteDiaMm));
    setDraftLengthM(1);
  };

  const startAdd = () => { reset(); setPicking(true); };
  const startChange = () => { reset(); setChanging(true); setPicking(true); };

  const handleConfirm = () => {
    let payload = null;
    if (tab === 'pick') {
      if (!pickedLineKey) { toast.error('Choose a Flashing row first'); return; }
      const row = flashingLines.find(l => l._key === pickedLineKey);
      if (!row) { toast.error('Row not found'); return; }
      payload = {
        uid: (crypto.randomUUID ? crypto.randomUUID() : `bt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
        line_key: row._key,
        template_id: row.flashing_template_id || '',
        template_name: row.flashing_type || '',
        type_name: row.type_name || '',
        width_mm: Number(row.girth_mm) || 0,
        length_m: Number(row.length) || 0,
        source: 'picked',
      };
    } else {
      if (!pickedTemplate) { toast.error('Choose a Backtray template first'); return; }
      const w = Math.max(100, Number(draftWidthMm) || 0);
      const l = Math.max(0, Number(draftLengthM) || 0);
      payload = {
        uid: (crypto.randomUUID ? crypto.randomUUID() : `bt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
        line_key: '',                // Parent will mint the new Flashing row's _key
        template_id: pickedTemplate._id || pickedTemplate.id || '',
        template_name: pickedTemplate.name || '',
        type_name: pickedTemplate.name || '',
        width_mm: w,
        length_m: l,
        source: 'created',
      };
    }
    if (changing) {
      onChangeBacktray?.(payload);
      toast.success(`Changed linked Backtray to ${payload.type_name || 'new row'}`);
    } else {
      onLinkBacktray?.(payload);
      toast.success(payload.source === 'created'
        ? `Created new Flashing row ${payload.type_name} and linked it`
        : `Linked existing Flashing row ${payload.type_name}`);
    }
    reset();
  };

  const handleUnlink = ({ deleteFlashing }) => {
    onUnlinkBacktray?.({ deleteFlashing });
    toast.success(deleteFlashing
      ? `Unlinked & deleted Flashing row ${linkedBacktray?.type_name || ''}`
      : `Unlinked Backtray (Flashing row stays)`);
  };

  return (
    <div className="col-span-12 w-full" data-testid={`comp-line-backtray-link-${ref_id}`}>
      <div className="flex items-center gap-2 flex-wrap pt-1">
        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-sky-700 shrink-0">
          Backtray
        </span>

        {/* Existing link chip */}
        {linkedBacktray ? (
          <div
            data-testid={`backtray-link-chip-${ref_id}`}
            className="inline-flex items-center gap-1 bg-sky-100 ring-1 ring-sky-300 rounded-full pl-2 pr-1 py-0.5 text-[10px]"
            title={`Linked Backtray (${linkedBacktray.source === 'created' ? 'auto-added by this cascade' : 'existing manual row'})`}
          >
            <span className="font-semibold text-sky-900">🔗 {fmtChip(linkedBacktray)}</span>
            <button
              type="button"
              data-testid={`backtray-link-change-${ref_id}`}
              onClick={startChange}
              className="ml-1 px-1 py-0.5 text-[9px] font-bold rounded text-amber-800 bg-amber-100 hover:bg-amber-200"
              title="Change which Backtray is linked"
            >✎</button>
            <button
              type="button"
              data-testid={`backtray-link-unlink-${ref_id}`}
              onClick={() => handleUnlink({ deleteFlashing: false })}
              className="px-1 py-0.5 text-[9px] font-bold rounded text-slate-700 bg-white hover:bg-slate-100 ring-1 ring-slate-200"
              title="Unlink only — Flashing row stays"
            >Unlink</button>
            {linkedBacktray.source === 'created' && (
              <button
                type="button"
                data-testid={`backtray-link-delete-${ref_id}`}
                onClick={() => handleUnlink({ deleteFlashing: true })}
                className="px-1 py-0.5 text-[9px] font-bold rounded text-rose-700 bg-rose-50 hover:bg-rose-100 ring-1 ring-rose-200"
                title="Unlink AND delete the auto-created Flashing row"
              >🗑</button>
            )}
          </div>
        ) : !picking ? (
          <span className="text-[10px] text-slate-400 italic">None — Dektite has no linked Backtray</span>
        ) : null}

        {/* Add / Cancel buttons */}
        {!linkedBacktray && !picking && (
          <button
            type="button"
            data-testid={`backtray-link-add-${ref_id}`}
            onClick={startAdd}
            className="px-2 py-1 text-[10px] font-bold rounded text-sky-800 bg-sky-50 hover:bg-sky-100 ring-1 ring-sky-200"
            title="Link a Backtray Flashing to this Dektite — pick existing or create new"
          >+ Has Backtray?</button>
        )}
        {picking && (
          <button
            type="button"
            data-testid={`backtray-link-cancel-${ref_id}`}
            onClick={reset}
            className="px-2 py-1 text-[10px] font-bold rounded text-slate-600 bg-white hover:bg-slate-50 ring-1 ring-slate-300"
          >Cancel</button>
        )}
      </div>

      {/* Picker */}
      {picking && (
        <div
          data-testid={`backtray-link-picker-${ref_id}`}
          className="mt-2 bg-sky-50/40 ring-1 ring-sky-200 rounded p-2 space-y-2"
        >
          {/* Tabs */}
          <div className="inline-flex rounded border border-sky-300 overflow-hidden">
            <button
              type="button"
              data-testid={`backtray-link-tab-pick-${ref_id}`}
              onClick={() => setTab('pick')}
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${tab === 'pick' ? 'bg-sky-600 text-white' : 'bg-white text-sky-700 hover:bg-sky-50'}`}
            >Pick existing</button>
            <button
              type="button"
              data-testid={`backtray-link-tab-create-${ref_id}`}
              onClick={() => setTab('create')}
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border-l border-sky-300 ${tab === 'create' ? 'bg-sky-600 text-white' : 'bg-white text-sky-700 hover:bg-sky-50'}`}
            >Create new</button>
          </div>

          {/* PICK tab */}
          {tab === 'pick' && (
            <div className="space-y-1.5">
              {backtrayRows.length === 0 && otherRows.length === 0 ? (
                <div className="text-[11px] text-slate-500 italic">
                  No Flashing rows on this appraisal yet. Switch to <strong>Create new</strong>.
                </div>
              ) : (
                <select
                  data-testid={`backtray-link-pick-select-${ref_id}`}
                  value={pickedLineKey}
                  onChange={(e) => setPickedLineKey(e.target.value)}
                  className="w-full text-[11px] px-2 py-1 border border-sky-300 rounded bg-white"
                >
                  <option value="">— Choose a Flashing row —</option>
                  {backtrayRows.length > 0 && (
                    <optgroup label="Backtrays (recommended)">
                      {backtrayRows.map((row) => (
                        <option key={row._key} value={row._key}>
                          {row.type_name || row.flashing_type || `(untitled — ${row._key.slice(0, 6)})`}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {otherRows.length > 0 && (
                    <optgroup label="Other flashings">
                      {otherRows.map((row) => (
                        <option key={row._key} value={row._key}>
                          {row.type_name || row.flashing_type || `(untitled — ${row._key.slice(0, 6)})`}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              )}
            </div>
          )}

          {/* CREATE tab */}
          {tab === 'create' && (
            <div className="space-y-2">
              {backtrayTemplates.length === 0 ? (
                <div className="text-[11px] text-slate-500 italic">
                  No Backtray templates available in the catalogue.
                </div>
              ) : (
                <FlashingTemplatePickerInline
                  ref_id={`bt-${ref_id}`}
                  eligible={backtrayTemplates}
                  picked={pickedTemplate}
                  cat="Back Tray"
                  roofProfileCtx={roofProfileCtx}
                  onPick={(tpl) => setPickedTemplate(tpl)}
                />
              )}
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] text-sky-800">
                  Width (mm) — suggested for Ø{dektiteDiaMm || '?'}
                  <input
                    type="number"
                    min="100"
                    step="50"
                    value={draftWidthMm}
                    onChange={(e) => setDraftWidthMm(Number(e.target.value) || 0)}
                    data-testid={`backtray-link-create-width-${ref_id}`}
                    className="w-full text-[11px] px-2 py-1 border border-sky-300 rounded bg-white"
                  />
                </label>
                <label className="text-[10px] text-sky-800">
                  Length (m)
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={draftLengthM}
                    onChange={(e) => setDraftLengthM(Number(e.target.value) || 0)}
                    data-testid={`backtray-link-create-length-${ref_id}`}
                    className="w-full text-[11px] px-2 py-1 border border-sky-300 rounded bg-white"
                  />
                </label>
              </div>
            </div>
          )}

          {/* Confirm */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid={`backtray-link-confirm-${ref_id}`}
              onClick={handleConfirm}
              disabled={tab === 'pick' ? !pickedLineKey : !pickedTemplate}
              className={`px-3 py-1.5 text-[11px] font-bold rounded ${
                (tab === 'pick' ? pickedLineKey : pickedTemplate)
                  ? 'bg-sky-600 hover:bg-sky-700 text-white'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >{changing ? 'Change link' : (tab === 'create' ? 'Create & Link' : 'Link Backtray')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
